import * as React from 'react';
import { Loader2 } from 'lucide-react';
import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';

import { cn } from '../lib/cn';

export const buttonVariants = cva(
  'inline-flex cursor-pointer whitespace-nowrap items-center justify-center gap-1.5 overflow-hidden text-base transition-[opacity,transform] active:scale-[0.98] motion-reduce:active:scale-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background disabled:pointer-events-none disabled:cursor-not-allowed',
  {
    variants: {
      variant: {
        default:
          'bg-background border border-border shadow hover:bg-item-hover active:bg-item-active disabled:bg-disabled disabled:text-gray-light',
        primary:
          'bg-primary text-background-secondary shadow hover:bg-primary-light active:bg-primary-dark disabled:bg-disabled disabled:text-gray-light',
        black:
          'bg-foreground text-background-secondary hover:bg-foreground-hover disabled:bg-disabled disabled:text-gray-light',
        outlined:
          'bg-transparent border border-border text-gray-dark hover:bg-item-hover active:bg-item-active disabled:text-gray-light',
        ghost:
          'bg-transparent text-gray-dark hover:bg-item-hover hover:text-foreground active:bg-item-active disabled:text-gray-light',
        link: 'bg-transparent text-gray-dark underline-offset-4 hover:text-foreground hover:underline disabled:text-gray-light',
        danger:
          'bg-danger-background text-danger-foreground hover:bg-danger-background-hover active:bg-danger-background-hover disabled:opacity-40',
        warning:
          'bg-warning-background text-warning-foreground hover:bg-warning-background-hover active:bg-warning-background-hover disabled:opacity-40',
        success:
          'bg-success-background text-success-foreground hover:bg-success-background-hover active:bg-success-background-hover disabled:opacity-40',
      },
      size: {
        small: 'h-6 text-sm',
        default: 'h-8',
        tall: 'h-9',
        large: 'h-10 text-md',
      },
      rounding: {
        default: 'rounded',
        full: 'rounded-full',
        large: 'rounded-lg',
        small: 'rounded-sm',
        none: 'rounded-none',
      },
      iconButton: {
        true: '',
        false: 'px-3.5',
      },
      block: {
        true: 'w-full',
        false: '',
      },
    },
    compoundVariants: [
      { iconButton: true, size: 'small', className: 'w-6 min-w-6 max-w-6 px-0' },
      { iconButton: true, size: 'default', className: 'w-8 min-w-8 max-w-8 px-0' },
      { iconButton: true, size: 'tall', className: 'w-9 min-w-9 max-w-9 px-0' },
      { iconButton: true, size: 'large', className: 'w-10 min-w-10 max-w-10 px-0' },
    ],
    defaultVariants: {
      variant: 'default',
      size: 'default',
      rounding: 'full',
      iconButton: false,
      block: false,
    },
  }
);

export const Button = React.forwardRef(
  (
    {
      className,
      variant,
      size,
      rounding,
      block,
      iconButton: iconButtonProp,
      asChild = false,
      loading = false,
      disabled,
      prefix,
      suffix,
      children,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button';
    const iconButton = iconButtonProp ?? (!children && !prefix && !suffix ? false : !children);

    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size, rounding, block, iconButton, className }))}
        disabled={disabled || loading}
        data-loading={loading || undefined}
        {...props}
      >
        {/* Slot takes exactly one element, so asChild passes the child through untouched. */}
        {asChild ? (
          children
        ) : (
          <>
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden />
            ) : (
              prefix && <span className="flex items-center [&_svg]:h-4 [&_svg]:w-4">{prefix}</span>
            )}
            {children && <span>{children}</span>}
            {suffix && <span className="flex items-center [&_svg]:h-4 [&_svg]:w-4">{suffix}</span>}
          </>
        )}
      </Comp>
    );
  }
);
Button.displayName = 'Button';
